import clsx from "clsx";
import DomPurify from "isomorphic-dompurify";
import { Check } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "components/Image";
import { getBlurUrl } from "components/Image/utils";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";
import { FadeIn } from "components/ui/FadeIn";
const MuxPlayer = dynamic(() => import("components/Tiles/commonComponents/MuxPlayer"));

interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

type SideBySideProps = {
  bgColor?: string;
  miniTitle?: string;
  title?: string;
  content?: string;
  showSectionsList?: boolean;
  disclaimer?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  isRounded?: boolean;
  shouldPrioritize?: boolean;
  orientation?: "left" | "right";
  src?: string;
  videoId?: string;
  sections?: {
    icon: string;
    title: string;
    disclaimer: string;
    content: string;
  }[];
  titleTag?: React.ElementType;
  subtitleTag?: React.ElementType;
  imageContain?: boolean;
  className?: string;
  useGifThumbnail?: boolean;
  verticalAlignment?: "start" | "center" | "end"; // New prop for vertical alignment
};

const SideBySide = ({
  bgColor,
  disclaimer,
  miniTitle,
  showSectionsList = true,
  isRounded = false,
  shouldPrioritize = false,
  title,
  content,
  button1,
  button2,
  orientation,
  src,
  videoId,
  sections,
  titleTag = "h2",
  subtitleTag = "p",
  imageContain = false,
  className,
  useGifThumbnail = false,
  verticalAlignment = "start" // Default to start alignment
}: SideBySideProps) => {
  const TitleTag = titleTag;
  const SubTitleTag = subtitleTag;
  const isImageRight = orientation === "right";
  const imageClassName = imageContain ? "object-contain" : "object-cover";

  // Create a mapping for vertical alignment classes
  const verticalAlignmentClasses = {
    start: "@5xl:items-start",
    center: "@5xl:items-center",
    end: "@5xl:items-end"
  };

  return (
    <section style={{ backgroundColor: bgColor }} id="sidebyside-38" className="w-full @container">
      <MaxWidthWrapper className="px-8 py-12 @5xl:py-24">
        <div
          className={clsx(
            "flex w-full flex-col !px-0 @5xl:justify-between @5xl:!px-20",
            isImageRight ? "@5xl:flex-row-reverse" : "@5xl:flex-row",
            // Apply vertical alignment based on the prop (always apply it, regardless of videoId)
            verticalAlignmentClasses[verticalAlignment],
            className
          )}
        >
          <div className="relative min-h-[250px] w-full @5xl:min-h-[450px] @5xl:w-6/12">
            {videoId ? (
              <div className="min-h-[250px] @5xl:min-h-[450px]">
                <MuxPlayer
                  playbackId={videoId}
                  loop
                  muted
                  maxResolution="2160p"
                  autoPlay
                  useGifThumbnail={useGifThumbnail}
                  className="border-primary-[#E8ECED] z-10  h-full w-full overflow-hidden rounded-xl border object-cover "
                />
              </div>
            ) : src ? (
              <Image
                alt={title || ""}
                src={src}
                className={clsx(imageClassName, isRounded && "rounded-xl")}
                placeholder="blur"
                blurDataURL={getBlurUrl()}
                quality={70}
                priority={shouldPrioritize}
                sizes="(max-width: 942px) 100vw, 50vw"
                fill
              />
            ) : null}
          </div>
          <div
            className={clsx(
              "w-full self-center px-0 @5xl:w-6/12 @5xl:py-10 @5xl:pl-0 @5xl:pr-8",
              isImageRight ? "@5xl:flex-row-reverse lg:pl-0 lg:pr-12" : "just flex justify-start @5xl:pl-14"
            )}
          >
            <div className="text-left">
              <div className="mt-12 lg:mt-2">
                {miniTitle && <SubTitleTag className="subby">{miniTitle}</SubTitleTag>}
                <TitleTag
                  className="@9xl:pt-0 @9xl:text-[42px] m-auto max-w-4xl pb-2  pt-4 font-heading text-[30px] leading-[1.2] text-primary-700  lg:pb-4"
                  dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title || "") }}
                />
                {content && <p className="text-base text-primary-700 @7xl:mt-4 lg:text-md">{content}</p>}
              </div>
              <div className="flex flex-col content-center justify-start pb-6 pt-2 text-left">
                {!!sections?.length &&
                  sections.map((item, i) => {
                    if (showSectionsList) {
                      return (
                        <ul
                          key={i}
                          role="list"
                          className="mt-3 max-w-[70%] space-y-1 text-md leading-6 text-primary-700"
                        >
                          <li className="flex gap-x-3">
                            <Check color="#017848" className="shrink-0" strokeWidth={1.4} size={25} />
                            {item.content}
                          </li>
                        </ul>
                      );
                    } else {
                      return (
                        <div
                          className="mb-5 inline-flex items-start gap-x-2 [text-wrap:balance]  lg:mb-12 lg:flex lg:gap-x-4"
                          key={i}
                        >
                          {item.icon && (
                            <div className="shrink-0 text-left">
                              <Image
                                alt={item.title}
                                src={item.icon}
                                width={48}
                                quality={70}
                                height={48}
                                placeholder="blur"
                                className="mb-2 inline-flex shrink-0"
                              />
                            </div>
                          )}
                          <div>
                            {item.title && (
                              <p className="mb-2 text-left font-heading text-md leading-none text-primary-700 @5xl:mb-3  lg:mb-2 lg:text-lg">
                                {item.title}
                              </p>
                            )}
                            {item.content && (
                              <p className="max-w-[98%] text-left  text-base text-primary-700   lg:max-w-[95%] lg:text-md">
                                {item.content}
                              </p>
                            )}
                            {item.disclaimer && (
                              <p className="max-w-[98%] pt-4 text-left text-xs  italic text-[#717171]   lg:max-w-[95%] lg:text-md">
                                {item.disclaimer}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    }
                  })}
              </div>
              {(button1 || button2) && (
                <FadeIn>
                  <DualButtonCTA button1={button1} button2={button2} isLeftAligned={true} />
                </FadeIn>
              )}
              {disclaimer && (
                <p className="max-w-[98%] pt-4 text-left text-sm  italic text-[#717171]   lg:max-w-[95%] ">
                  {disclaimer}
                </p>
              )}
            </div>
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default SideBySide;